import type {
  EventCategoryChip,
  EventPageView,
  EventSourceBadge,
  PublicEventEntry,
} from "../../src/eventPage.ts";
import { eventPageUrl } from "../../constants.ts";
import type { EventEntry, StoredEvent } from "../../src/fields.ts";
import { type Category, categoryEmoji, cats } from "../../src/category.ts";
import { eventPageUiTexts } from "../../src/eventPageTexts.ts";
import type { Language } from "../../src/texts.ts";
import { currencySymbolForCountry } from "../../src/countries.ts";

export const isRtlText = (text: string): boolean =>
  /[\u0590-\u05ff\u0600-\u06ff]/.test(text);

const extractCoords = (coordinates: unknown): [number, number] | undefined => {
  if (!coordinates) return undefined;
  if (
    Array.isArray(coordinates) &&
    coordinates.length === 2 &&
    coordinates.every((x) => typeof x === "number")
  ) {
    return [coordinates[0], coordinates[1]];
  }
  if (
    typeof coordinates === "object" &&
    coordinates !== null &&
    "coordinates" in coordinates
  ) {
    const arr = (coordinates as { coordinates: unknown }).coordinates;
    if (
      Array.isArray(arr) &&
      arr.length === 2 &&
      arr.every((x) => typeof x === "number")
    ) {
      return [arr[0], arr[1]];
    }
  }
  return undefined;
};

export const buildGoogleMapsUrl = ({
  venue,
  location,
  city,
  country,
  coordinates,
}: {
  venue?: string;
  location?: string;
  city?: string;
  country?: string;
  coordinates?: unknown;
}): string | undefined => {
  const isAccurate = typeof coordinates === "object" &&
    coordinates !== null &&
    "isAccurate" in coordinates &&
    Boolean((coordinates as { isAccurate?: boolean }).isAccurate);
  const coords = extractCoords(coordinates);

  if (isAccurate && coords) {
    const [lng, lat] = coords;
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  }

  const parts: string[] = [];
  if (venue) parts.push(venue);
  if (
    location &&
    (!venue || !venue.toLowerCase().includes(location.toLowerCase()))
  ) {
    parts.push(location);
  }
  if (
    city &&
    !parts.some((p) => p.toLowerCase().includes(city.toLowerCase()))
  ) {
    parts.push(city);
  }
  if (
    country &&
    !parts.some((p) => p.toLowerCase().includes(country.toLowerCase()))
  ) {
    parts.push(country);
  }

  if (parts.length === 0) {
    if (coords) {
      const [lng, lat] = coords;
      return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    }
    return undefined;
  }

  return `https://www.google.com/maps/search/?api=1&query=${
    encodeURIComponent(parts.join(", "))
  }`;
};

export const buildGoogleCalendarUrl = ({
  name,
  description,
  location,
  start,
  end,
  url,
}: {
  name: string;
  description?: string;
  location?: string;
  start: number;
  end?: number;
  url?: string;
}): string => {
  const effectiveEnd = end && end > start ? end : start + 2 * 60 * 60 * 1000;
  const formatUtc = (ms: number) =>
    new Date(ms).toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
  const dates = `${formatUtc(start)}/${formatUtc(effectiveEnd)}`;
  const details = [
    description,
    url ? `Event link: ${url}` : "",
  ].filter(Boolean).join("\n\n");
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: name,
    dates,
    details,
    ...(location ? { location } : {}),
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
};

export const eventPriceText = (
  language: Language,
  categories: Category[],
  priceRange?: { start: number; end: number },
  country?: string,
): string | undefined => {
  const { donationBased, freeEntrance } = eventPageUiTexts[language];
  const symbol = currencySymbolForCountry(country);
  return categories.includes("donation based") &&
      (!priceRange || priceRange.end === 0)
    ? donationBased
    : priceRange
    ? priceRange.end > 0
      ? priceRange.start === priceRange.end
        ? `${priceRange.start}${symbol}`
        : `${priceRange.start}-${priceRange.end}${symbol}`
      : freeEntrance
    : undefined;
};

const extractSourceBadgeFromEntry = (
  event: EventEntry | PublicEventEntry,
  language: Language,
): EventSourceBadge | undefined => {
  const entry = event as EventEntry;
  const isHe = language === "he";
  const apiSubmit = entry.apiSubmit;
  const userSubmit = entry.userSubmit;

  if (apiSubmit) {
    if (
      apiSubmit.sourceKind === "instagram-page" ||
      apiSubmit.source === "instagram-stories"
    ) {
      const handle = apiSubmit.sourceRef
        ? (apiSubmit.sourceRef.startsWith("@")
          ? apiSubmit.sourceRef
          : `@${apiSubmit.sourceRef}`)
        : undefined;
      return {
        kind: "instagram",
        label: handle ?? "",
        url: apiSubmit.sourceRef
          ? `https://www.instagram.com/${
            apiSubmit.sourceRef.replace(/^@/, "")
          }/`
          : undefined,
        originalText: apiSubmit.text?.slice(0, 500),
      };
    }
    if (
      apiSubmit.sourceKind === "whatsapp-group" ||
      apiSubmit.source === "supergreen"
    ) {
      return {
        kind: "whatsapp",
        label: "",
      };
    }
    if (apiSubmit.sourceKind === "link-scrape-source" || apiSubmit.source) {
      const rawRef = apiSubmit.sourceRef;
      const isSensitive = rawRef && (
        rawRef.includes("@g.us") ||
        rawRef.includes("@c.us") ||
        rawRef.includes("@s.whatsapp.net")
      );
      const url = !isSensitive && rawRef?.startsWith("http")
        ? rawRef
        : apiSubmit.source?.startsWith("http")
        ? apiSubmit.source
        : undefined;
      const name = isSensitive ? undefined : (rawRef || apiSubmit.source);
      return {
        kind: "web",
        label: name || (isHe ? "מקור חיצוני" : "Web Source"),
        url,
        originalText: apiSubmit.text?.slice(0, 500),
      };
    }
  }

  if (userSubmit?.text) {
    return {
      kind: "user",
      label: isHe ? "דיווח קהילתי" : "Community Submission",
    };
  }

  return undefined;
};

export const buildClientEventView = (
  event: EventEntry | PublicEventEntry,
  language: Language = "en",
): EventPageView => {
  const structured: StoredEvent = event.structured;
  const isHe = language === "he";
  const locale = isHe ? "he-IL" : "en-US";
  const startDate = new Date(structured.timestamp.start);

  const dayName = startDate
    .toLocaleDateString(locale, { weekday: "long" })
    .replace(/^יום\s+/, "");
  const dateText = `${startDate.getDate()}/${
    startDate.getMonth() + 1
  }/${startDate.getFullYear()}`;

  const startTime = startDate.toLocaleTimeString(locale, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const hasValidEnd = Boolean(
    structured.timestamp.end &&
      structured.timestamp.end > structured.timestamp.start,
  );
  const endDateObj = hasValidEnd ? new Date(structured.timestamp.end) : null;
  const endDateText = endDateObj
    ? `${endDateObj.getDate()}/${
      endDateObj.getMonth() + 1
    }/${endDateObj.getFullYear()}`
    : undefined;
  const isMultiDay = Boolean(endDateText && dateText !== endDateText);
  const endDayName = isMultiDay && endDateObj
    ? endDateObj
      .toLocaleDateString(locale, { weekday: "long" })
      .replace(/^יום\s+/, "")
    : undefined;

  const endTime = endDateObj
    ? endDateObj.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    })
    : null;

  const timeText = endTime ? `${startTime} – ${endTime}` : startTime;

  const locParts: string[] = [];
  if (structured.venue) locParts.push(structured.venue);
  if (
    structured.location &&
    (!structured.venue ||
      !structured.venue.toLowerCase().includes(
        structured.location.toLowerCase(),
      ))
  ) {
    locParts.push(structured.location);
  }
  if (
    structured.city &&
    !locParts.some((p) =>
      p.toLowerCase().includes(structured.city!.toLowerCase())
    )
  ) {
    locParts.push(structured.city);
  }

  const locationText = locParts.join(", ") ||
    eventPageUiTexts[language].locationNotSpecified;

  const mapsUrl = buildGoogleMapsUrl({
    venue: structured.venue,
    location: structured.location,
    city: structured.city,
    country: structured.country,
    coordinates: structured.coordinates,
  });

  const calendarUrl = buildGoogleCalendarUrl({
    name: structured.name,
    description: structured.description,
    location: locationText,
    start: structured.timestamp.start,
    end: structured.timestamp.end,
    url: eventPageUrl(event.id),
  });

  const categories: EventCategoryChip[] = structured.categories.map((cat) => ({
    emoji: categoryEmoji(cat) || "",
    label: cats[cat]?.display?.[language] || cat,
  }));

  const accessibilityText = structured.wheelchairAccessible === true
    ? (structured.accessibilityNotes
      ? `${
        eventPageUiTexts[language].wheelchairAccessible
      } (${structured.accessibilityNotes})`
      : eventPageUiTexts[language].wheelchairAccessible)
    : structured.accessibilityNotes || undefined;

  return {
    dayName,
    dateText,
    timeText,
    priceText: eventPriceText(
      language,
      structured.categories,
      structured.priceRange,
      structured.country,
    ),
    locationText,
    city: structured.city,
    mapsUrl,
    categories,
    calendarUrl,
    source: extractSourceBadgeFromEntry(event, language),
    isSoldOut: structured.soldOut,
    accessibilityText,
    isMultiDay,
    endDayName,
    endDateText: isMultiDay ? endDateText : undefined,
    startTime,
    endTime: endTime || undefined,
  };
};
